/**
 * Doorly SmartStart™ — 6-module Homebuyer Success Program curriculum.
 *
 * Each module: title, estimatedMinutes, content (sections), quiz (3 Qs).
 * Quiz: passes at 2/3 correct on first attempt; 1/3 or 0/3 allows one
 * retry; passes regardless on retry (we want completion, not gatekeeping).
 * Backend records the score for admin visibility regardless.
 */
export const SMARTSTART_MODULES = [
  {
    number: 1,
    title: 'Know Your Credit',
    estimatedMinutes: 8,
    content: [
      {
        heading: 'How Credit Scores Work',
        body: `Your credit score is a three-digit number between 300 and 850 that tells lenders how reliably you've repaid debts in the past. The most widely used scoring model is FICO. Your score is calculated from five factors: payment history (35%), amounts owed (30%), length of credit history (15%), new credit (10%), and credit mix (10%).`,
      },
      {
        heading: 'What the Three Bureaus Track',
        body: `Three companies — Equifax, Experian, and TransUnion — collect and maintain your credit data independently. Your score may differ slightly across all three because not every lender reports to all three bureaus. Doorly uses a tri-merged credit report that pulls from all three and uses your middle score (or lower score if only two are available) as your qualifying FICO.`,
      },
      {
        heading: 'What Moves the Needle',
        body: `The fastest legitimate ways to improve your score: pay every bill on time (even one missed payment can drop your score significantly), reduce your credit card balances to below 30% of your limit, avoid opening new credit accounts right before applying for a mortgage, and don't close old accounts — length of history matters.`,
      },
      {
        heading: 'What Doorly Looks For',
        body: `Doorly's minimum qualifying FICO is 560. A score of 620 or above can qualify you for a larger loan and a lower rate. We look beyond your score — your payment behavior, savings habits, and income stability all matter. A higher score usually means a better rate.`,
      },
    ],
    quiz: [
      {
        question: 'Which factor has the biggest impact on your FICO score?',
        options: ['Length of credit history', 'Payment history', 'Credit mix', 'New credit inquiries'],
        correctIndex: 1,
      },
      {
        question: 'How many credit bureaus does Doorly pull from?',
        options: ['One', 'Two', 'Three', 'Four'],
        correctIndex: 2,
      },
      {
        question: 'What credit utilization rate is generally recommended to maintain a healthy score?',
        options: ['Below 10%', 'Below 30%', 'Below 50%', 'Below 75%'],
        correctIndex: 1,
      },
    ],
  },
  {
    number: 2,
    title: 'Your Money, Your Mortgage',
    estimatedMinutes: 8,
    content: [
      {
        heading: 'Debt-to-Income Ratio Explained',
        body: `Your debt-to-income ratio (DTI) is one of the most important numbers in mortgage underwriting. It compares your total monthly debt payments to your gross monthly income. If you earn $6,000/month and pay $2,400/month in debts (including your new mortgage payment), your DTI is 40%. Doorly's maximum DTI is 50% depending on your loan size and credit profile.`,
      },
      {
        heading: 'What Counts as Debt',
        body: `Monthly debt obligations include: your new mortgage payment (principal, interest, taxes, and insurance), car payments, student loans, credit card minimum payments, personal loans, and any alimony or child support. Utilities, subscriptions, and groceries are not counted.`,
      },
      {
        heading: 'How to Reduce Your DTI Before Closing',
        body: `Pay down or pay off installment loans with 10 or fewer payments remaining — they can be excluded from your DTI. Pay credit card balances to zero (the account doesn't need to be closed). Avoid taking on any new debt between now and closing. Do not finance a car, open a new credit card, or co-sign on any loan during this period.`,
      },
      {
        heading: 'Budgeting for Homeownership',
        body: `Your mortgage payment is only part of the cost of owning a home. Budget for property taxes, homeowner's insurance, HOA fees (if applicable), and ongoing maintenance — typically 1-2% of the home's value per year. Building a 3-6 month emergency fund before closing gives you a financial cushion for unexpected repairs.`,
      },
    ],
    quiz: [
      {
        question: 'If you earn $5,000/month and have $1,750 in monthly debt payments, what is your DTI?',
        options: ['25%', '30%', '35%', '40%'],
        correctIndex: 2,
      },
      {
        question: 'Which of these is NOT counted as a monthly debt obligation in mortgage qualifying?',
        options: ['Car payment', 'Student loan payment', 'Monthly grocery bill', 'Credit card minimum payment'],
        correctIndex: 2,
      },
      {
        question: 'What should you avoid doing between loan approval and closing?',
        options: ['Paying down credit card balances', 'Saving additional funds', 'Financing a new vehicle', 'Reviewing your loan estimate'],
        correctIndex: 2,
      },
    ],
  },
  {
    number: 3,
    title: 'The Doorly AFM™ Program',
    estimatedMinutes: 8,
    content: [
      {
        heading: 'How the Ability-First Mortgage™ Works',
        body: `The Doorly Ability-First Mortgage™ (AFM) is a 30-year fixed-rate mortgage designed for borrowers who are financially capable but overlooked by traditional mortgage systems. Instead of requiring a conventional credit profile, we evaluate your full financial picture — income, savings behavior, rental history, and ability to repay.`,
      },
      {
        heading: 'The Dual-Transaction Model',
        body: `Here's what makes Doorly different: when you find a home, Doorly acquires it directly from the seller in a cash-backed purchase. Then, on the same day, Doorly sells it to you through a first-lien mortgage. This means you can compete with cash offers even as a financed buyer. You take title at closing just like any other homeowner.`,
      },
      {
        heading: 'Your Rights as a Borrower',
        body: `As a Doorly borrower you have the right to receive a Loan Estimate within 3 business days of application, a Closing Disclosure at least 3 business days before closing, a copy of your appraisal, and fair treatment regardless of race, color, religion, national origin, sex, marital status, or age. You also have the right to cancel certain transactions within 3 days under the Right of Rescission.`,
      },
      {
        heading: 'What to Expect from Application Through Closing',
        body: `After pre-qualification, you'll receive a SmartEstimate showing your estimated loan terms. Once you find a home, Doorly generates a Loan Estimate. After your appraisal package, you'll review your Closing Disclosure at least 3 days before closing. At closing, you sign your loan documents and receive your keys. Doorly targets a 20-30 day closing timeline.`,
      },
    ],
    quiz: [
      {
        question: 'What type of mortgage is the Doorly AFM™?',
        options: ['Adjustable-rate, 15-year', 'Fixed-rate, 30-year', 'Interest-only, 10-year', 'Balloon payment, 5-year'],
        correctIndex: 1,
      },
      {
        question: "In Doorly's dual-transaction model, who purchases the home from the seller first?",
        options: ['The borrower directly', 'A title company', 'Doorly', 'A warehouse lender'],
        correctIndex: 2,
      },
      {
        question: 'How many days before closing must you receive your Closing Disclosure?',
        options: ['1 business day', '3 business days', '5 business days', '7 business days'],
        correctIndex: 1,
      },
    ],
  },
  {
    number: 4,
    title: 'Fair Housing & Your Rights',
    estimatedMinutes: 8,
    content: [
      {
        heading: 'The Equal Credit Opportunity Act (ECOA)',
        body: `The ECOA prohibits lenders from discriminating against credit applicants based on race, color, religion, national origin, sex, marital status, age, or because you receive public assistance. This means Doorly must evaluate your application based solely on your creditworthiness — not on who you are or where you live.`,
      },
      {
        heading: 'The Fair Housing Act',
        body: `The Fair Housing Act protects homebuyers from discrimination in the sale, rental, and financing of housing. It is illegal for a seller, real estate agent, or lender to treat you differently based on race, color, national origin, religion, sex, familial status, or disability. This protection extends to every step of your homebuying process.`,
      },
      {
        heading: 'What to Do If You Experience Discrimination',
        body: `If you believe you've been treated unfairly, you have the right to file a complaint. You can contact the Consumer Financial Protection Bureau (CFPB) at consumerfinance.gov, the U.S. Department of Housing and Urban Development (HUD) at hud.gov, or your state's regulatory agency. You can also contact Doorly directly at support@godoorly.com and we will take your concern seriously.`,
      },
      {
        heading: 'Your Right to an Explanation',
        body: `If your application is denied or you receive less favorable terms than requested, you have the right to know why. Doorly is required to send you an Adverse Action Notice within 30 days explaining the specific reasons for any adverse decision. You also have the right to request a free copy of your credit report within 60 days of receiving an adverse action notice.`,
      },
    ],
    quiz: [
      {
        question: 'Which law prohibits mortgage lenders from discriminating based on race, sex, or national origin?',
        options: ['The Fair Debt Collection Practices Act', 'The Equal Credit Opportunity Act', 'The Truth in Lending Act', 'The Homeowners Protection Act'],
        correctIndex: 1,
      },
      {
        question: 'How many days does Doorly have to send an Adverse Action Notice after denying an application?',
        options: ['7 days', '15 days', '30 days', '60 days'],
        correctIndex: 2,
      },
      {
        question: 'Where can you file a fair housing complaint?',
        options: ['Only with your lender', 'The CFPB or HUD', 'Only with a private attorney', 'The Federal Reserve'],
        correctIndex: 1,
      },
    ],
  },
  {
    number: 5,
    title: 'Predatory Lending & Consumer Protection',
    estimatedMinutes: 8,
    content: [
      {
        heading: 'Red Flags to Watch For',
        body: `Predatory lenders use high-pressure tactics, unclear terms, and deceptive practices to take advantage of borrowers. Warning signs include: being pressured to sign quickly without time to review, being offered terms very different from what was discussed, unexplained fees added at closing, being encouraged to borrow more than you need, and lenders who discourage you from seeking a second opinion.`,
      },
      {
        heading: 'How to Read a Loan Estimate',
        body: `Your Loan Estimate will be provided once a property has been identified for your purchase. The LE shows your estimated loan terms, projected monthly payment, and closing costs. Review it carefully — if anything looks different from what you were told, ask before proceeding.`,
      },
      {
        heading: 'Understanding Fees',
        body: `Some fees are fixed ("cannot increase") and some can change. Origination charges, for example, cannot increase between the Loan Estimate and Closing Disclosure. Third-party fees like appraisal and title can change within limits. Ask the Doorly team to explain any fee you don't recognize. You have the right to a clear explanation of every charge.`,
      },
      {
        heading: 'RESPA Protections',
        body: `The Real Estate Settlement Procedures Act (RESPA) protects you from illegal kickbacks and referral fees between settlement service providers. It also requires lenders to provide you with an itemized list of settlement costs and prohibits lenders from requiring you to use a specific title company or insurance provider. Doorly is required to comply with all RESPA provisions.`,
      },
    ],
    quiz: [
      {
        question: 'Which of these is a red flag for predatory lending?',
        options: ['Being given 3 days to review your Closing Disclosure', 'Being pressured to sign documents immediately', 'Receiving a Loan Estimate within 3 business days', 'Being offered a fixed interest rate'],
        correctIndex: 1,
      },
      {
        question: 'What document shows you the estimated costs and terms of your mortgage before closing?',
        options: [
          'The Closing Disclosure',
          'The Loan Estimate',
          'The Promissory Note',
          'The Title Commitment',
        ],
        correctIndex: 1,
      },
      {
        question: 'What does RESPA protect you from?',
        options: ['Rising interest rates', 'Illegal kickbacks between settlement service providers', 'Property tax increases', 'HOA fee disputes'],
        correctIndex: 1,
      },
    ],
  },
  {
    number: 6,
    title: 'Life as a Homeowner',
    estimatedMinutes: 8,
    content: [
      {
        heading: 'Understanding Your Escrow Account',
        body: `Doorly requires an escrow (impound) account for all loans. Each month, a portion of your mortgage payment is set aside to pay your property taxes and homeowner's insurance when they come due. Your lender manages these payments on your behalf. Your escrow amount may adjust annually based on changes in your tax or insurance bills — this can cause your monthly payment to change slightly each year.`,
      },
      {
        heading: 'Budgeting for Maintenance',
        body: `Homeownership comes with ongoing costs beyond your mortgage payment. A common rule of thumb is to budget 1% of your home's value per year for maintenance and repairs. On a $400,000 home that's $4,000/year or about $333/month. Build this into your budget from day one. Starting a dedicated home maintenance savings fund gives you a cushion for unexpected repairs like a roof replacement or HVAC failure.`,
      },
      {
        heading: 'What to Do If You Miss a Payment',
        body: `Life happens. If you're struggling to make a payment, contact Doorly as early as possible — before you miss a payment if you can. Options may include a short-term forbearance, a repayment plan, or a loan modification. Waiting until you're 60+ days late significantly limits your options and damages your credit. Communication is always better than silence.`,
      },
      {
        heading: 'Building Long-Term Wealth Through Homeownership',
        body: `Every mortgage payment builds equity — the portion of your home you actually own. As your loan balance decreases and your home's value potentially increases, your equity grows. This equity can be accessed later through a refinance or home equity loan for major expenses. Homeownership has historically been one of the most reliable paths to long-term wealth building for American families.`,
      },
    ],
    quiz: [
      {
        question: 'What is an escrow account used for in a mortgage?',
        options: ['To hold your down payment until closing', 'To pay property taxes and insurance on your behalf', 'To collect your monthly P&I payment', 'To fund home repairs'],
        correctIndex: 1,
      },
      {
        question: 'What is a commonly recommended annual budget for home maintenance?',
        options: ['0.25% of home value', '0.5% of home value', '1% of home value', '3% of home value'],
        correctIndex: 2,
      },
      {
        question: 'If you are struggling to make a mortgage payment, what should you do first?',
        options: ['Wait and see if the situation improves', 'Contact your lender as early as possible', 'Stop making payments until you can afford them', 'Sell the home immediately'],
        correctIndex: 1,
      },
    ],
  },
];

export const TOTAL_MODULES = SMARTSTART_MODULES.length;

// Pass threshold per spec — score is # of correct answers (0–3).
// 2 or 3 correct on the first attempt advances; below that triggers a
// retry. After the retry the borrower is allowed through regardless
// (the goal is completion, not gatekeeping). The backend records the
// final score for admin visibility.
export const QUIZ_PASS_THRESHOLD = 2;
